/**
 * Pure reseller admin-read derivations (CLA-902).
 *
 * Server-side helpers that turn a reseller's persisted profile (+ optional live
 * Stripe onboarding read) into the enriched list/detail read model the admin web
 * UI needs: a derived invite status, a four-step readiness breakdown, and the
 * single source of truth for which installation statuses count as torn-down.
 *
 * Every function here is pure (no Effect) so it is trivially unit-testable and
 * shared verbatim by both the list SQL mapping and the detail read.
 *
 * @since 1.0.0
 */
import type { AgentInstallationStatus } from "@fm/db/models/AgentInstallation";
import type { StripeAccountStatus } from "@fm/db/models/ResellerProfile";
import type { RateTermsConfig } from "@fm/db/models/ResellerRateTerms";
import { DateTime, Schema } from "effect";
import type { OnboardingStatusResult } from "./resellers.rpc.ts";

/**
 * The admin-facing lifecycle of a reseller's start invite, as the list/detail
 * screens render it:
 *
 * - `not_invited` — no invite has been sent yet.
 * - `pending` — an invite is out and still within its window.
 * - `expired` — the invite window has passed (and it was never accepted).
 * - `accepted` — the invitee bound their identity.
 *
 * Named `Admin…` on purpose: the CLIENT barrel already exports a reseller-facing
 * `ResellerInviteStatus` = Literal("valid", "accepted", "expired") from
 * `authenticated/reseller.rpc.ts` — a different domain.
 *
 * @since 1.0.0
 */
export const AdminResellerInviteStatus = Schema.Literal("not_invited", "pending", "expired", "accepted");
export type AdminResellerInviteStatus = Schema.Schema.Type<typeof AdminResellerInviteStatus>;

/**
 * The invite timestamps `deriveInviteStatus` reads off a reseller profile — the
 * narrow subset so both the raw list-select row and the profile row satisfy it.
 * @since 1.0.0
 */
export interface ResellerInviteTimestamps {
  readonly inviteSentAt: DateTime.Utc | null;
  readonly inviteExpiresAt: DateTime.Utc | null;
  readonly inviteAcceptedAt: DateTime.Utc | null;
}

/**
 * Derive the admin invite status from a reseller's invite timestamps at `now`.
 * Precedence is accepted > expired (`inviteExpiresAt <= now`) > pending >
 * not_invited: an accepted invite reads `accepted` even past its window, and a
 * never-sent invite reads `not_invited`. The clock is injected (never read
 * inside this pure fn) so tests are deterministic and the exact-expiry boundary
 * is coverable.
 * @since 1.0.0
 */
export const deriveInviteStatus = (
  profile: ResellerInviteTimestamps,
  now: DateTime.Utc,
): AdminResellerInviteStatus => {
  if (profile.inviteAcceptedAt !== null) return "accepted";
  if (profile.inviteSentAt === null) return "not_invited";
  if (profile.inviteExpiresAt !== null && DateTime.lessThanOrEqualTo(profile.inviteExpiresAt, now)) {
    return "expired";
  }
  return "pending";
};

/**
 * Whether the reseller has BOTH default rate terms set (`install` and
 * `subscription`) — the "rates set" readiness gate. Ported server-side from the
 * `claw reseller doctor` CLI so the web reads readiness from the API, not a
 * duplicated helper.
 * @since 1.0.0
 */
export const hasBothDefaultTerms = (rateTerms: RateTermsConfig | null): boolean =>
  rateTerms !== null && rateTerms.default.install !== undefined && rateTerms.default.subscription !== undefined;

/**
 * The four-step reseller readiness breakdown the admin overview renders: has a
 * connected account been created, is Stripe onboarding complete, are default
 * rate terms set, and is at least one customer attributed.
 * @since 1.0.0
 */
export const ResellerReadiness = Schema.Struct({
  accountCreated: Schema.Boolean,
  onboardingComplete: Schema.Boolean,
  ratesSet: Schema.Boolean,
  firstCustomerAttributed: Schema.Boolean,
});
export type ResellerReadiness = Schema.Schema.Type<typeof ResellerReadiness>;

/**
 * The profile fields `deriveReadiness` reads — the connected-account id +
 * persisted status and the rate terms.
 * @since 1.0.0
 */
export interface ResellerReadinessProfile {
  readonly stripeAccountId: string | null;
  readonly stripeAccountStatus: StripeAccountStatus | null;
  readonly rateTerms: RateTermsConfig | null;
}

/**
 * Derive the four readiness flags. `onboardingComplete` uses the LIVE Stripe read
 * (`chargesEnabled && payoutsEnabled`) when one is supplied, otherwise the
 * persisted-status fallback (`stripeAccountStatus === "active"`) — the list makes
 * no Stripe call and passes `live: null`; the detail also passes `null` (the web
 * combines it with a separate live `getOnboardingStatus`).
 * @since 1.0.0
 */
export const deriveReadiness = (
  profile: ResellerReadinessProfile,
  attributedCustomerCount: number,
  live: OnboardingStatusResult | null,
): ResellerReadiness => ({
  accountCreated: profile.stripeAccountId !== null,
  onboardingComplete: live !== null
    ? live.chargesEnabled && live.payoutsEnabled
    : profile.stripeAccountStatus === "active",
  ratesSet: hasBothDefaultTerms(profile.rateTerms),
  firstCustomerAttributed: attributedCustomerCount > 0,
});

/**
 * The installation statuses that count as TORN DOWN — an agent in one of these
 * is no longer live and is excluded from a reseller's active-agent count. The
 * single source of truth: both the list SQL and the detail's active-agent read
 * derive their `status NOT IN (...)` predicate from this constant.
 * @since 1.0.0
 */
export const TORN_DOWN_INSTALL_STATUSES = ["deleted", "deprovisioned"] as const satisfies ReadonlyArray<
  AgentInstallationStatus
>;

/**
 * The single installation status that counts as a live, ACTIVE agent for a
 * reseller's "Active agents" overview stat. Only installs in status `active`
 * qualify — `provisioning`, `pending_install`, `stopped` and `error` installs
 * are NOT active (they are excluded), as are the torn-down statuses
 * (see {@link TORN_DOWN_INSTALL_STATUSES}). The detail's active-agent read
 * derives its `status = ...` predicate from this constant. Distinct from the
 * list/customers/picker "Agents" columns, which count every not-torn-down
 * install.
 * @since 1.0.0
 */
export const ACTIVE_INSTALL_STATUS = "active" as const satisfies AgentInstallationStatus;
